<template>
  <fieldset
    :class="fieldsetClasses"
    :data-cy="props.name ? `ds-fieldset--${props.name}` : 'ds-fieldset'"
    :disabled="props.disabled"
    :aria-labelledby="isCollapsible && !slots.label ? headerLabelId : undefined"
    :aria-label="isCollapsible && slots.label ? props.label : undefined"
  >
    <template v-if="isCollapsible">
      <div
        :class="DS_FIELDSET_CLASSES.Header"
        :data-cy="props.name ? `ds-fieldset__header--${props.name}` : 'ds-fieldset__header'"
      >
        <span
          v-if="slots.label"
          :class="DS_FIELDSET_CLASSES.Label"
          data-cy="ds-fieldset__label"
        >
          <DSIcon
            v-if="props.icon"
            :icon="props.icon"
            size="sm"
          />
          <slot name="label" />
          <span
            v-if="props.required"
            :class="DS_LABEL_CLASSES.RequiredAsterisk"
            data-cy="ds-label__required-asterisk"
          >
            *
          </span>
        </span>
        <button
          v-ds-tooltip="props.tooltip"
          :class="DS_FIELDSET_CLASSES.Toggle"
          type="button"
          :aria-label="slots.label ? props.label : undefined"
          :aria-expanded="isExpanded"
          :aria-controls="bodyId"
          :disabled="props.disabled"
          data-cy="ds-fieldset__toggle"
          @click="toggleExpanded"
        >
          <template v-if="!slots.label">
            <DSIcon
              v-if="props.icon"
              :icon="props.icon"
              size="sm"
            />
            <span :id="headerLabelId">
              <DSLabelContent
                :label="props.label || ''"
                :required="props.required"
                :hidden-label="props.hiddenLabel"
              />
            </span>
          </template>
          <DSIcon
            :icon="isExpanded ? 'chevron-top' : 'chevron-down'"
            size="sm"
          />
        </button>

        <span
          v-if="!props.withBorder && props.withDivider"
          :class="DS_FIELDSET_CLASSES.HeaderDivider"
          data-cy="ds-fieldset__header-divider"
          aria-hidden="true"
        />

        <DSFieldsetActions
          v-if="props.removable || slots.actions"
          :removable="props.removable"
          :disabled="props.disabled"
          @remove="remove"
        >
          <slot name="actions" />
        </DSFieldsetActions>
      </div>

      <div
        v-if="isExpanded"
        :id="bodyId"
        :class="[DS_FIELDSET_CLASSES.Content, DS_FIELDSET_CLASSES.Body]"
        data-cy="ds-fieldset__body"
      >
        <slot />
      </div>
    </template>

    <template v-else>
      <DSFieldsetActions
        v-if="props.removable || slots.actions"
        :removable="props.removable"
        :disabled="props.disabled"
        @remove="remove"
      >
        <slot name="actions" />
      </DSFieldsetActions>

      <DSLegend
        v-if="label"
        :label="label"
        :name="name"
        :icon="props.icon"
        :required="props.required"
        :disabled="props.disabled"
        :has-error="hasError"
        :inline-edit="props.inlineEdit"
        :tooltip="props.tooltip"
        :hidden-label="props.hiddenLabel"
        :with-divider="!props.withBorder && props.withDivider"
      />

      <div :class="DS_FIELDSET_CLASSES.Content">
        <slot />
      </div>
    </template>
  </fieldset>
</template>

<script setup lang="ts">
import { DS_DIRECTION } from "@/src/design_system/shared/types/direction";
import { computed, type VNode } from "vue";
import { DS_FIELDSET_CLASSES } from "@/src/design_system/form_elements/fieldset/constants/classes";
import DSLegend from "@/src/design_system/form_elements/fieldset/components/DSLegend.vue";
import DSFieldsetActions from "@/src/design_system/form_elements/fieldset/components/DSFieldsetActions.vue";
import { DSFieldsetProps } from "@/src/design_system/form_elements/fieldset/types/props";
import { useStateClasses } from "@/src/design_system/shared/composables/useStateClasses";
import { DSFieldsetEmits } from "@/src/design_system/form_elements/fieldset/types/emits";
import DSIcon from "@/src/design_system/primitives/icon/components/DSIcon.vue";
import DSLabelContent from "@/src/design_system/form_elements/label/components/DSLabelContent.vue";
import { DS_LABEL_CLASSES } from "@/src/design_system/form_elements/label/constants/classes";
import { generateUUID } from "@/src/shared/utils/uuid";
import { vDsTooltip } from "@/src/design_system/primitives/tooltip/directives/vDsTooltip";

const props = withDefaults(defineProps<DSFieldsetProps>(), {
  label: undefined,
  name: undefined,
  icon: undefined,
  direction: DS_DIRECTION.Vertically,
  required: false,
  disabled: false,
  inlineEdit: false,
  withDivider: true,
  removable: false,
  withBorder: false,
  errorMessages: undefined,
  tooltip: undefined,
  collapsible: false,
  hiddenLabel: false,
});

const emits = defineEmits<DSFieldsetEmits>();
const expanded = defineModel<boolean>("expanded", { default: true });

// The `label` slot renders beside the toggle because interactive content is not allowed inside a button.
const slots = defineSlots<{
  default?: () => VNode[];
  label?: () => VNode[];
  actions?: () => VNode[];
}>();

const remove = () => emits("remove");

const bodyId = `ds-fieldset-body-${generateUUID()}`;
const headerLabelId = `ds-fieldset-header-label-${generateUUID()}`;

const isCollapsible = computed(() => props.collapsible && Boolean(props.label));

const isExpanded = computed(() => expanded.value);

const hasError = computed(() => (props.errorMessages?.length ?? 0) > 0);

const toggleExpanded = () => {
  if (props.disabled) return;
  expanded.value = !isExpanded.value;
};

const { stateClasses } = useStateClasses(props);

const fieldsetClasses = computed(() => ({
  [DS_FIELDSET_CLASSES.Root]: true,
  [DS_FIELDSET_CLASSES.Border]: props.withBorder && !isCollapsible.value,
  [DS_FIELDSET_CLASSES.Collapsible]: isCollapsible.value,
  [DS_FIELDSET_CLASSES.Collapsed]: isCollapsible.value && !isExpanded.value,
  [DS_FIELDSET_CLASSES.Horizontal]: props.direction === DS_DIRECTION.Horizontally,
  [DS_FIELDSET_CLASSES.Vertical]: props.direction === DS_DIRECTION.Vertically,
  ...stateClasses.value,
}));
</script>
